import type { ExperimentEditAction } from "@v1/features/experiments/contexts/ExperimentEditContext";
import type { Experiment } from "schemas/generated/experiment";
import type { Variation } from "schemas/generated/variation";

import * as React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { ExperimentEditContext } from "@v1/features/experiments/contexts/ExperimentEditContext";
import { useExperimentDetails } from "@v1/features/experiments/tabs/hooks/useExperimentDetails";
import { Loader } from "@v2/components/Loader";
import { useForm } from "react-hook-form";
import { Outlet } from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { experimentSchema } from "schemas/experiment";
import { v4 as uuid } from "uuid";

import { positiveIntToCapitalLetter } from "./utils";

// NOTE (Kurt, 2024-12-03): This function calculates the allocation percentages
// for the variations in an experiment. It ensures that the total percentage
// is 100% and that the allocation percentages are distributed evenly, with
// any remainder added to the first variation.
function calculateVariationPercentages(variations: Variation[]) {
  const totalVariations = variations.length;
  const basePercentage = Math.floor(100 / totalVariations);
  const remainder = 100 % totalVariations;

  return variations.map((variation, index) => ({
    ...variation,
    allocationPercent:
      index === 0 ? basePercentage + remainder : basePercentage,
  }));
}

const LoadedExperimentEditWrapper: React.FC<{
  experiment: Experiment;
}> = ({ experiment }) => {
  const {
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { isDirty, errors, isValid },
    getValues,
  } = useForm<Experiment>({
    resolver: zodResolver(experimentSchema),
    defaultValues: experiment,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const handleSetValue = (
    key: keyof Experiment,
    value: Experiment[keyof Experiment],
    shouldDirty = true,
  ) => {
    setValue(key, value, {
      shouldDirty,
      shouldValidate: true,
    });
  };

  const dispatchExperimentEdit = (action: ExperimentEditAction) => {
    const variations = watch("variations");
    exhaustiveSwitch(
      action,
      "type",
    )({
      addVariation: () => {
        let nextSlug = `Variant-${positiveIntToCapitalLetter(variations.length + 1).toUpperCase()}`;
        if (variations.find((v) => v.slug === nextSlug)) {
          nextSlug = uuid().slice(0, 8);
        }

        const newVariation = {
          id: uuid(),
          target: null,
          slug: nextSlug,
          allocationPercent: 0,
        };

        const updatedVariations = calculateVariationPercentages([
          ...variations,
          newVariation,
        ]);

        handleSetValue("variations", updatedVariations);
      },
      setEqualVariationSplit: () => {
        const updatedVariations = calculateVariationPercentages(variations);
        handleSetValue("variations", updatedVariations);
      },
      changeVariation: (action) => {
        handleSetValue(
          "variations",
          variations.map((v) => {
            return v.id === action.payload.id ? { ...v, ...action.payload } : v;
          }),
        );
      },
      removeVariation: (action) => {
        const remainingVariations = variations.filter(
          (variation) => variation.id !== action.payload,
        );

        const updatedVariations =
          calculateVariationPercentages(remainingVariations);

        handleSetValue("variations", updatedVariations);
      },
      changeProperty: (action) => {
        const { key, value, shouldDirty } = action.payload;
        handleSetValue(key, value, shouldDirty);
      },
    });
  };

  return (
    <ExperimentEditContext.Provider
      value={{
        watch,
        reset,
        dispatchExperimentEdit,
        hasDataChanged: isDirty,
        isExperimentValid: isValid,
        handleReactHookFormSubmit: handleSubmit,
        errors,
        getValues,
      }}
    >
      <Outlet />
    </ExperimentEditContext.Provider>
  );
};

const ExperimentEditWrapper: React.FC = () => {
  const { experiment } = useExperimentDetails();

  // NOTE (Kurt, 2025-01-25): Only when we don't have an experiment should we show
  // the loader, isLoadingExperiment is true when we update an experiment, and we
  // don't want to show a loader when an experiment is updated.
  if (!experiment) {
    return <Loader />;
  }

  return <LoadedExperimentEditWrapper experiment={experiment} />;
};

export default ExperimentEditWrapper;
